"use client";

import { useState } from "react";
import {
  ChevronLeft,
  Zap,
  Users,
  Clock,
  PhoneCall,
  Play,
  Loader2,
} from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { Badge } from "@/app/_module/components/ui/badge";
import { ScrollArea } from "@/app/_module/components/ui/scroll-area";
import { useDialerSessionList } from "@/app/_module/hooks/useDialerSessions";
import type { DialerSession, DialerSessionLead } from "@/app/_module/services/dialerSessionService";

interface DialerSessionHistoryProps {
  onBack: () => void;
  onResume: (session: DialerSession) => void;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return `${hrs}h ${remainMins}m`;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  if (isToday) return `Today, ${time}`;
  if (isYesterday) return `Yesterday, ${time}`;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  }) + `, ${time}`;
}

function getStatusConfig(status: string) {
  switch (status) {
    case "completed":
      return { label: "Completed", color: "bg-emerald-100 text-emerald-700" };
    case "stopped":
      return { label: "Stopped", color: "bg-amber-100 text-amber-700" };
    case "active":
      return { label: "Active", color: "bg-blue-100 text-blue-700" };
    case "paused":
      return { label: "Paused", color: "bg-gray-100 text-gray-600" };
    default:
      return { label: status, color: "bg-gray-100 text-gray-600" };
  }
}

const PAGE_SIZE = 20;

export function DialerSessionHistory({ onBack, onResume }: DialerSessionHistoryProps) {
  const [limit, setLimit] = useState(PAGE_SIZE);
  const { data, isLoading, error } = useDialerSessionList(true, 1, limit);
  const sessions = data?.sessions || [];
  const total = data?.total || 0;
  const hasMore = sessions.length < total;

  const canResume = (session: DialerSession) => {
    // Session must be in a resumable state
    if (!["stopped", "paused", "active"].includes(session.status)) return false;
    // Must have leads in the queue to dial
    if (!session.leadQueue || session.leadQueue.length === 0) return false;

    const mc = session.maxConcurrent ?? (session as any).max_concurrent ?? 1;
    const isPower = mc <= 1 || session.dialMode === "power_dial";

    if (isPower) {
      // Power dialer persists the FULL original queue and tracks position with
      // currentLeadIndex — resumable if there are leads past the cursor.
      const idx = session.currentLeadIndex ?? 0;
      return idx < session.leadQueue.length;
    }

    // Multi-line: leadQueue already holds ONLY the remaining/re-queued leads
    // (the frontend rewrites it on every progress update). If any remain, it's
    // resumable. The old code ALSO subtracted completedCalls here, which
    // double-counted the dialed leads: as completedCalls accumulated across
    // resumes, `leadQueue.length - completedCalls` went <= 0 and the Resume
    // button vanished after ~3 resumes even though leads still remained.
    return session.leadQueue.length > 0;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-3 py-2 border-b border-gray-200 flex items-center gap-2 flex-shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="h-7 w-7 p-0"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h3 className="text-sm font-semibold">Session History</h3>
        <span className="text-xs text-muted-foreground ml-auto">Last 30 days</span>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-2">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && (
            <div className="text-center py-12 text-sm text-muted-foreground">
              Failed to load sessions
            </div>
          )}

          {!isLoading && !error && sessions.length === 0 && (
            <div className="text-center py-12">
              <PhoneCall className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No sessions yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Start a Power Dialer or Multi-Line session to see it here
              </p>
            </div>
          )}

          {sessions.map((session) => {
            const statusConfig = getStatusConfig(session.status);
            // Use maxConcurrent to distinguish: 1 = power dialer, >1 = multi-line
            const mc = session.maxConcurrent ?? (session as any).max_concurrent ?? 1;
            const isPower = mc <= 1;
            const resumable = canResume(session);
            const completed = session.completedCalls ?? session.totalCalls ?? 0;
            const progressPct = session.totalLeadsToCall > 0
              ? Math.round((completed / session.totalLeadsToCall) * 100)
              : 0;
            // Compute dial time: use totalDialTime if available, otherwise derive from session duration
            const dialTime = session.totalDialTime || (
              session.endedAt && session.startedAt
                ? Math.floor((new Date(session.endedAt).getTime() - new Date(session.startedAt).getTime()) / 1000)
                : 0
            );

            return (
              <div
                key={session.id}
                className="rounded-lg border border-gray-200 bg-white p-3 space-y-2 hover:border-gray-300 transition-colors"
              >
                {/* Row 1: Mode + Date */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    {isPower ? (
                      <Zap className="h-3.5 w-3.5 text-black" />
                    ) : (
                      <Users className="h-3.5 w-3.5 text-black" />
                    )}
                    <span className="text-xs font-medium">
                      {isPower ? "Power Dialer" : "Multi-Line"}
                    </span>
                    {session.source === "dialiq" && (
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.14em] font-bold bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded border border-violet-200">
                        <span className="h-1 w-1 rounded-full bg-violet-500" />
                        DialIQ
                      </span>
                    )}
                  </div>
                  <span className="text-[11px] text-muted-foreground">
                    {formatDate(session.startedAt)}
                  </span>
                </div>

                {/* Row 2: Stats */}
                <div className="flex items-center gap-3 text-[11px] text-gray-600">
                  <span>
                    {completed}/{session.totalLeadsToCall} leads called
                  </span>
                  <span className="flex items-center gap-0.5">
                    <PhoneCall className="h-3 w-3" />
                    {session.totalAnswered || 0} answered
                  </span>
                  <span className="flex items-center gap-0.5">
                    <Clock className="h-3 w-3" />
                    {formatDuration(dialTime)}
                  </span>
                </div>

                {/* Progress bar */}
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-300 bg-primary"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>

                {/* Row 3: Status + Outcome + Resume */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Badge
                      variant="secondary"
                      className={`text-[10px] px-1.5 py-0 h-4 border-0 ${statusConfig.color}`}
                    >
                      {statusConfig.label}
                    </Badge>
                    {/* Outcome badges */}
                    {(session.outcome?.appointments || 0) > 0 && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 border-0 bg-blue-100 text-blue-700">
                        {session.outcome.appointments} Appt
                      </Badge>
                    )}
                    {(session.outcome?.sold || 0) > 0 && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 border-0 bg-emerald-100 text-emerald-700">
                        {session.outcome.sold} Sold
                      </Badge>
                    )}
                  </div>

                  {resumable && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 text-[10px] px-2 gap-1"
                      onClick={() => onResume(session)}
                    >
                      <Play className="h-3 w-3" />
                      Resume
                    </Button>
                  )}
                </div>
              </div>
            );
          })}

          {/* Load More */}
          {hasMore && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs text-muted-foreground"
              onClick={() => setLimit((prev) => prev + PAGE_SIZE)}
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : null}
              Load more sessions ({total - sessions.length} remaining)
            </Button>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
